import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/config/queries';
import {
  createDiaryMemory,
  deleteDiaryMemory,
  fetchDiaryContext,
  fetchDiaryMemories,
  requestDiaryMemorySuggestion,
  toggleDiaryMemoryPin,
  updateDiaryMemory,
} from '@/lib/data/diary';
import type {
  DiaryContext,
  DiaryMemory,
  DiaryMemoryDraft,
  DiaryMemorySuggestionRequest,
  DiarySummary,
} from '@/types/diary';

export async function invalidateDiaryQueries(queryClient: QueryClient, userId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DIARY_MEMORIES(userId) }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DIARY_CONTEXT(userId) }),
  ]);
}

export function summarizeDiaryMemories(memories: DiaryMemory[]): DiarySummary {
  return memories.reduce<DiarySummary>(
    (summary, memory) => {
      summary.total += 1;
      summary.lastUpdatedAt =
        !summary.lastUpdatedAt || memory.updated_at > summary.lastUpdatedAt
          ? memory.updated_at
          : summary.lastUpdatedAt;

      if (memory.is_pinned) {
        summary.pinned += 1;
      }

      if (memory.memory_type === 'constraint') {
        summary.constraints += 1;
      }

      if (memory.memory_type === 'medical') {
        summary.medical += 1;
      }

      return summary;
    },
    {
      total: 0,
      pinned: 0,
      constraints: 0,
      medical: 0,
      lastUpdatedAt: null,
    },
  );
}

export function useDiaryMemories(
  userId?: string,
  options?: {
    limit?: number;
    includeArchived?: boolean;
  },
) {
  return useQuery({
    queryKey: QUERY_KEYS.DIARY_MEMORIES(userId ?? 'anonymous', options?.limit),
    queryFn: () => fetchDiaryMemories(options),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useDiaryContext(userId?: string, limit = 12) {
  return useQuery<DiaryContext>({
    queryKey: QUERY_KEYS.DIARY_CONTEXT(userId ?? 'anonymous', limit),
    queryFn: () => fetchDiaryContext(limit),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useDiarySummary(userId?: string, limit = 24) {
  const query = useDiaryMemories(userId, { limit });
  const summary = useMemo(() => summarizeDiaryMemories(query.data ?? []), [query.data]);

  return {
    ...query,
    summary,
  };
}

export function useDiaryMemorySuggestions() {
  const suggestMutation = useMutation({
    mutationFn: (input: DiaryMemorySuggestionRequest) => requestDiaryMemorySuggestion(input),
  });

  return {
    suggestMutation,
  };
}

export function useDiaryMemoryManager(userId?: string) {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (draft: DiaryMemoryDraft) => createDiaryMemory(draft),
    onSuccess: async () => {
      if (!userId) return;
      await invalidateDiaryQueries(queryClient, userId);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ memoryId, draft }: { memoryId: string; draft: DiaryMemoryDraft }) =>
      updateDiaryMemory(memoryId, draft),
    onSuccess: async () => {
      if (!userId) return;
      await invalidateDiaryQueries(queryClient, userId);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (memoryId: string) => deleteDiaryMemory(memoryId),
    onSuccess: async () => {
      if (!userId) return;
      await invalidateDiaryQueries(queryClient, userId);
    },
  });

  const togglePinMutation = useMutation({
    mutationFn: ({ memoryId, isPinned }: { memoryId: string; isPinned: boolean }) =>
      toggleDiaryMemoryPin(memoryId, isPinned),
    onSuccess: async () => {
      if (!userId) return;
      await invalidateDiaryQueries(queryClient, userId);
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    togglePinMutation,
  };
}
